// Round intel summary: a client-side accumulation of the round's story —
// a score timeline plus per-team tallies of planes downed, ships sunk,
// ground razed, convoys raided/delivered, fire missions and torpedo hits,
// and the round's top guns — rendered as a card at round end. Pure
// bookkeeping over the event stream already broadcast (see the event
// handlers in main.js, which feed this via note/noteKill); nothing new
// crosses the wire.

import { TEAM_INFO } from "./planes.js";
import { insigniaHTML } from "./insignia.js";

const TEAM_CLASS = ["crimson", "cobalt"];

// One ordered tally per team. Keys match the note() calls in main.js.
function blankTeam() {
  return { kills: 0, ships: 0, ground: 0, trucks: 0, supplies: 0, fires: 0, torps: 0 };
}

// Stat rows shown per team column, in display order.
const ROWS = [
  ["kills", "✚", "planes downed"],
  ["ships", "⚓", "ships sunk"],
  ["torps", "🐟", "torpedo hits"],
  ["ground", "💥", "ground razed"],
  ["trucks", "🚚", "convoy hits"],
  ["supplies", "📦", "supplies in"],
  ["fires", "📡", "fire missions"],
];

const esc = (s) => String(s).replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);

export class RoundIntel {
  constructor(root) {
    this.root = root; // #intel-card
    this.headEl = root.querySelector("#intel-head");
    this.subEl = root.querySelector("#intel-sub");
    this.canvas = root.querySelector("#intel-timeline");
    this.colsEl = root.querySelector("#intel-cols");
    this.topEl = root.querySelector("#intel-top");
    // Click / tap anywhere on the card dismisses it early.
    root.addEventListener("pointerdown", () => this.hide());
    this.reset();
  }

  /** Wipe accumulated state at the start of each round. */
  reset() {
    this.teams = [blankTeam(), blankTeam()];
    this.pilotKills = new Map(); // id -> kills this round
    this.timeline = []; // {t, c, b} score samples, t = elapsed seconds
    this._startedAt = null;
    this._lastSampleAt = -1;
    this.mode = "skyring";
  }

  /** Increment a team tally (no-op for environment/-1 teams). */
  note(team, key, n = 1) {
    if ((team === 0 || team === 1) && key in this.teams[team]) this.teams[team][key] += n;
  }

  /** Credit a plane shoot-down to the killer's team and, if a pilot, to them. */
  noteKill(killerTeam, killerId) {
    this.note(killerTeam, "kills");
    if (killerId) this.pilotKills.set(killerId, (this.pilotKills.get(killerId) ?? 0) + 1);
  }

  /** Sample the running score; throttled to ~1 Hz of wall-clock time. */
  sample(now, crimson, cobalt, mode) {
    this.mode = mode;
    if (this._startedAt === null) this._startedAt = now;
    if (now - this._lastSampleAt < 1) return;
    this._lastSampleAt = now;
    this.timeline.push({ t: now - this._startedAt, c: crimson, b: cobalt });
    if (this.timeline.length > 1800) this.timeline.shift();
  }

  hide() {
    this.root.classList.add("hidden");
  }

  /**
   * Render and show the card. Called from the roundEnd handler with the
   * final score and roster so it can name the top guns; auto-dismissed by
   * the caller when the next round starts.
   */
  show({ winner, mode, scoreToWin, fleet, roster, selfTeam, spectating }) {
    this.mode = mode;
    const fleetMode = mode === "fleet";

    // Header: result line in the winner's colour.
    let head, color;
    if (winner === -1) {
      head = "ROUND DRAW";
      color = "#cfd6df";
    } else {
      const won = !spectating && winner === selfTeam;
      head = won ? `${TEAM_INFO[winner].name} WINS — VICTORY!` : `${TEAM_INFO[winner].name} WINS`;
      color = TEAM_INFO[winner].cssColor;
    }
    this.headEl.textContent = head;
    this.headEl.style.color = color;

    // Subline: mode plus the figure that actually settled it.
    const last = this.timeline.at(-1);
    if (fleetMode && fleet) {
      const pct = (f) => (f <= 0 ? "SUNK" : `${Math.max(1, Math.round(f * 100))}%`);
      this.subEl.innerHTML =
        `FLEET ACTION &middot; ` +
        `<span class="crimson">CRIMSON ⚓ ${pct(fleet.c)}</span> &nbsp; ` +
        `<span class="cobalt">COBALT ⚓ ${pct(fleet.b)}</span>`;
    } else {
      // Naval and land are points races (kills + domination zones); the air
      // game is the Sky Ring.
      const label = mode === "naval" ? "FLEET ACTION" : mode === "land" ? "GROUND WAR" : "SKY RING";
      this.subEl.innerHTML =
        `${label} &middot; first to ${scoreToWin} &middot; ` +
        `<span class="crimson">${last?.c ?? 0}</span> – ` +
        `<span class="cobalt">${last?.b ?? 0}</span>`;
    }

    this._drawTimeline(scoreToWin);
    this._renderColumns();
    this._renderTopGuns(roster);

    this.root.classList.remove("hidden");
  }

  _drawTimeline(scoreToWin) {
    const cv = this.canvas;
    const ctx = cv.getContext("2d");
    const W = cv.width;
    const H = cv.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "rgba(8, 12, 20, 0.55)";
    ctx.fillRect(0, 0, W, H);

    const pts = this.timeline;
    if (pts.length < 2) {
      ctx.fillStyle = "#6c7a89";
      ctx.font = "12px 'Courier New', monospace";
      ctx.textAlign = "center";
      ctx.fillText("round too short to chart", W / 2, H / 2);
      ctx.textAlign = "start";
      return;
    }

    const tMax = pts.at(-1).t || 1;
    let yMax = 1;
    for (const p of pts) yMax = Math.max(yMax, p.c, p.b);
    // In Sky Ring the win line anchors the scale; fleet points are open-ended.
    const showWinLine = this.mode !== "fleet" && scoreToWin > 0;
    if (showWinLine) yMax = Math.max(yMax, scoreToWin);

    const padL = 8;
    const padR = 8;
    const padT = 10;
    const padB = 10;
    const px = (t) => padL + (t / tMax) * (W - padL - padR);
    const py = (v) => H - padB - (v / yMax) * (H - padT - padB);

    if (showWinLine && scoreToWin <= yMax) {
      ctx.strokeStyle = "rgba(244, 197, 66, 0.4)";
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(padL, py(scoreToWin));
      ctx.lineTo(W - padR, py(scoreToWin));
      ctx.stroke();
      ctx.setLineDash([]);
    }

    const line = (key, stroke) => {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 2;
      ctx.beginPath();
      pts.forEach((p, i) => {
        const X = px(p.t);
        const Y = py(p[key]);
        if (i) ctx.lineTo(X, Y);
        else ctx.moveTo(X, Y);
      });
      ctx.stroke();
    };
    line("c", TEAM_INFO[0].cssColor);
    line("b", TEAM_INFO[1].cssColor);
  }

  _renderColumns() {
    const col = (team) => {
      const t = this.teams[team];
      const rows = ROWS.map(
        ([key, icon, label]) =>
          `<div class="intel-row"><span class="intel-ico">${icon}</span>` +
          `<span class="intel-val">${t[key]}</span>` +
          `<span class="intel-lbl">${label}</span></div>`,
      ).join("");
      return (
        `<div class="intel-col ${TEAM_CLASS[team]}">` +
        `<h3>${TEAM_INFO[team].name}</h3>${rows}</div>`
      );
    };
    this.colsEl.innerHTML = col(0) + col(1);
  }

  _renderTopGuns(roster) {
    const top = [...this.pilotKills.entries()]
      .filter(([, k]) => k > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
    if (!top.length) {
      this.topEl.innerHTML = "";
      return;
    }
    const rows = top
      .map(([id, kills], i) => {
        const r = roster.get(id);
        const name = esc(r?.name ?? "someone");
        const cls = TEAM_CLASS[r?.team ?? 0];
        const title = r?.title ? ` <span class="intel-title">${esc(r.title)}</span>` : "";
        return (
          `<div class="intel-ace"><span class="intel-rank">${i + 1}</span>` +
          `<span class="${cls}">${insigniaHTML(r?.ins ?? "")}${name}</span>${title}` +
          `<span class="intel-kills">${kills} ✚</span></div>`
        );
      })
      .join("");
    this.topEl.innerHTML = `<h4>TOP GUNS</h4>${rows}`;
  }
}
